
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './Button';
import { Page } from '../types';

interface CookieConsentProps {
    onNavigate?: (page: Page) => void;
}

type ConsentSettings = {
    essential: boolean;
    analytics: boolean;
    marketing: boolean;
    externalMedia: boolean;
};

export const CookieConsent: React.FC<CookieConsentProps> = ({ onNavigate }) => {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    const [settings, setSettings] = useState<ConsentSettings>({
        essential: true,
        analytics: false,
        marketing: false,
        externalMedia: false
    });

    useEffect(() => {
        const savedConsent = localStorage.getItem('cookie-consent-settings');
        if (!savedConsent) {
            setTimeout(() => setIsVisible(true), 1000);
        }
    }, []);

    const saveSettings = (newSettings: ConsentSettings) => {
        localStorage.setItem('cookie-consent-settings', JSON.stringify(newSettings));
        window.dispatchEvent(new Event('cookie-consent-updated'));
        setIsVisible(false);
    };

    const handleAcceptAll = () => {
        saveSettings({ essential: true, analytics: true, marketing: true, externalMedia: true });
    };

    const handleDeclineAll = () => {
        saveSettings({ essential: true, analytics: false, marketing: false, externalMedia: false });
    };

    const handleSaveSelection = () => {
        saveSettings(settings);
    };

    const toggleCategory = (category: keyof ConsentSettings) => {
        if (category === 'essential') return;
        setSettings(prev => ({ ...prev, [category]: !prev[category] }));
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-10px_40px_rgba(0,0,0,0.2)] animate-in slide-in-from-bottom-full duration-500 font-sans">
            <div className="max-w-4xl mx-auto">

                {/* Main Header */}
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-6">
                    <div className="bg-clever-teal/10 p-3 rounded-xl hidden md:block">
                        <Cookie size={32} className="text-clever-teal" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-bold text-gray-900 text-lg mb-2 flex items-center gap-2">
                            <span className="md:hidden"><Cookie size={20} className="text-clever-teal" /></span>
                            {t('cookie.title')}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed max-w-2xl">
                            {t('cookie.description')}
                            <br />
                            <a href="#" onClick={(e) => { e.preventDefault(); onNavigate?.('privacy'); window.scrollTo(0, 0); }} className="text-clever-teal hover:underline font-medium text-xs mt-1 inline-block">
                                {t('cookie.privacy_link')}
                            </a>
                        </p>
                    </div>
                </div>

                {/* Detailed Settings (Expandable) */}
                {showDetails && (
                    <div className="bg-gray-50 rounded-xl p-6 mb-6 border border-gray-200 space-y-4 animate-in fade-in duration-300">

                        {/* Essential */}
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-bold text-gray-900 text-sm">{t('cookie.essential')}</h4>
                                    <span className="bg-clever-teal text-white text-[10px] px-2 py-0.5 rounded-full font-bold tracking-wider">{t('cookie.required')}</span>
                                </div>
                                <p className="text-xs text-gray-500">{t('cookie.essential_desc')}</p>
                            </div>
                            <div className="relative inline-flex items-center cursor-not-allowed opacity-50">
                                <input type="checkbox" checked={true} disabled className="sr-only peer" />
                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-clever-teal"></div>
                            </div>
                        </div>

                        <div className="h-px bg-gray-200"></div>

                        {/* Analytics */}
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h4 className="font-bold text-gray-900 text-sm mb-1">{t('cookie.analytics')}</h4>
                                <p className="text-xs text-gray-500">{t('cookie.analytics_desc')}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" checked={settings.analytics} onChange={() => toggleCategory('analytics')} className="sr-only peer" />
                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-clever-teal"></div>
                            </label>
                        </div>

                        <div className="h-px bg-gray-200"></div>

                        {/* Marketing */}
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h4 className="font-bold text-gray-900 text-sm mb-1">{t('cookie.marketing')}</h4>
                                <p className="text-xs text-gray-500">{t('cookie.marketing_desc')}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" checked={settings.marketing} onChange={() => toggleCategory('marketing')} className="sr-only peer" />
                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-clever-teal"></div>
                            </label>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <button
                        onClick={() => setShowDetails(!showDetails)}
                        className="text-xs font-bold text-gray-500 hover:text-gray-800 flex items-center gap-1 transition-colors w-full md:w-auto justify-center md:justify-start"
                    >
                        {showDetails ? (
                            <><ChevronUp size={14} /> {t('cookie.hide_settings')}</>
                        ) : (
                            <><ChevronDown size={14} /> {t('cookie.show_settings')}</>
                        )}
                    </button>

                    <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                        {showDetails ? (
                            <Button variant="outline" onClick={handleSaveSelection} className="justify-center text-xs py-3 border-gray-300 text-gray-700">
                                {t('cookie.save_selection')}
                            </Button>
                        ) : (
                            <Button variant="outline" onClick={handleDeclineAll} className="justify-center text-xs py-3 border-gray-300 text-gray-700 hover:bg-gray-100">
                                {t('cookie.decline')}
                            </Button>
                        )}

                        <Button variant="primary" onClick={handleAcceptAll} className="justify-center text-xs py-3 bg-clever-teal hover:bg-teal-600 text-white shadow-lg shadow-teal-500/20 w-full md:w-auto">
                            {t('cookie.accept_all')}
                        </Button>
                    </div>
                </div>

            </div>
        </div>
    );
};
